import React, { useEffect, useState } from "react";
import { Button, Checkbox, Icon } from "antd";
import { useDispatch, useSelector } from "react-redux";

import { hidePollPopup } from "../../containers/App/actions";
import { selectPollPopup } from "../../containers/App/selectors";
import { AutoResizeTextArea } from "../AutoResizeTextArea";
import { PollAnswerButtonI, PollFollowUpI } from "../../typings/interfaces";
import styles from "./pollPopup.scss";

// Which answer button opened the follow-up currently on screen.
interface OpenFollowUpI {
  isYes: boolean;
  followUp: PollFollowUpI;
}

export const PollPopup = () => {
  const dispatch = useDispatch();
  const poll = useSelector(selectPollPopup);

  const [openFollowUp, setOpenFollowUp] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // A page can hide the popup on its own (e.g. on unmount) without going
  // through the handlers below, so reset the form whenever the poll changes.
  const pollId = poll ? poll.pollId : null;
  useEffect(() => {
    setOpenFollowUp(null);
    setSelectedOptions([]);
    setComment("");
  }, [pollId]);

  if (!poll) {
    return null;
  }

  const {
    title,
    yesAnswer,
    noAnswer,
    onCancel,
    onSubmit
  } = poll;

  const close = () => {
    setOpenFollowUp(null);
    setSelectedOptions([]);
    setComment("");
    dispatch(hidePollPopup());
  };

  // The X icon cancels the whole poll, on either step.
  const handleCancel = () => {
    if (onCancel) {
      onCancel(poll);
    }
    close();
  };

  const handleAnswer = (answer: PollAnswerButtonI, isYes: boolean) => () => {
    if (answer.onClick) {
      answer.onClick(poll);
    }

    if (answer.followUp) {
      setOpenFollowUp({ isYes, followUp: answer.followUp });
      return;
    }

    close();
  };

  const handleOptionChange = (value: string | number, checked: boolean) => {
    setSelectedOptions(prev =>
      checked ? [...prev, value] : prev.filter(option => option !== value)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!openFollowUp) {
      return;
    }

    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(
          {
            pollId: poll.pollId,
            isYes: openFollowUp.isYes,
            options: selectedOptions,
            comment
          },
          poll
        );
      }
      close();
    } finally {
      setSubmitting(false);
    }
  };

  const renderAnswerButton = (answer: PollAnswerButtonI, isYes: boolean) => (
    <button
      type="button"
      className={styles.button}
      onClick={handleAnswer(answer, isYes)}
    >
      <Icon type={isYes ? "like" : "dislike"} style={{ marginRight: 8 }} />
      <span>{answer.text || (isYes ? "Da" : "Ne")}</span>
    </button>
  );

  const renderFollowUp = ({ followUp }: OpenFollowUpI) => (
    <form onSubmit={handleSubmit}>
      <div className={styles.options}>
        {followUp.options.map(option => (
          <Checkbox
            key={option.value}
            checked={selectedOptions.includes(option.value)}
            onChange={e => handleOptionChange(option.value, e.target.checked)}
            style={{ marginLeft: 0 }}
          >
            {option.label}
          </Checkbox>
        ))}
      </div>
      <AutoResizeTextArea
        className={styles.comment}
        placeholder={followUp.commentPlaceholder || "Komentar (nije obavezno)"}
        value={comment}
        onChange={e => setComment(e.target.value)}
      />
      <div className={styles.submitRow}>
        <Button
          htmlType="submit"
          type="primary"
          disabled={selectedOptions.length === 0 || submitting}
          loading={submitting}
        >
          {followUp.submitText || "Pošalji"}
        </Button>
      </div>
    </form>
  );

  return (
    <div className={styles.pollPopup}>
      <div className={styles.header}>
        <div className={styles.title}>
          {openFollowUp ? openFollowUp.followUp.title : title}
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={handleCancel}
          aria-label="Zatvori"
        >
          <Icon type="close" />
        </button>
      </div>
      {openFollowUp ? (
        renderFollowUp(openFollowUp)
      ) : (
        <div className={styles.actions}>
          {yesAnswer && renderAnswerButton(yesAnswer, true)}
          {noAnswer && renderAnswerButton(noAnswer, false)}
        </div>
      )}
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/PollPopup/PollPopup.tsx